import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Button,
    TextField,
    Divider,
    CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import FinancialAnalysisViewer from "../../component-files/viewers/FinancialAnalysisViewer";

const FinancialInsights = () => {
    const theme = useTheme();

    const [selectedMonth, setSelectedMonth] = useState("01");
    const [selectedYear, setSelectedYear] = useState("2024");

    const [customPrompt, setCustomPrompt] = useState("");
    const [chatHistory, setChatHistory] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [responseDetails, setResponseDetails] = useState(null);
    const chatRef = useRef(null);

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [chatHistory]);

    const groupAndSummarizeData = (arr, type) =>
        arr.reduce((acc, item) => {
            const cat = item[`${type}Category`];
            const sub = item[`${type}Subcategory`];
            const amt = item.Amount;
            const key = new Date(item[`${type}Date`]).toLocaleString("default", {
                month: "short",
                year: "numeric",
            });
            acc[key] ??= {};
            acc[key][cat] ??= {};
            acc[key][cat][sub] = (acc[key][cat][sub] || 0) + amt;
            return acc;
        }, {});

    const parseResponse = (text = "") => {
        const labels = ["Performance", "Trends", "Improvement", "Excellence", "Summary"];
        const out = {};
        labels.forEach((lbl) => {
            const rgx = new RegExp(
                `${lbl}\\s*[:\\-]?\\s*([\\s\\S]*?)(?=(?:${labels.join("|")})\\s*[:\\-]|$)`,
                "i"
            );
            const m = text.match(rgx);
            out[lbl] = m ? m[1].replace(/\s+/g, " ").trim() : "No data available.";
        });
        return out;
    };

    const handleGenerateInsights = async () => {
        if (!selectedMonth || !selectedYear) {
            return alert("Please select both a month and a year.");
        }

        // Compute first & last day of the selected month
        const year = parseInt(selectedYear, 10);
        const monthIndex = parseInt(selectedMonth, 10) - 1; // 0‑11
        const firstDay = new Date(year, monthIndex, 1);
        const lastDay = new Date(year, monthIndex + 1, 0);
        const startDateStr = firstDay.toISOString().split("T")[0];
        const endDateStr = lastDay.toISOString().split("T")[0];

        setChatHistory((h) => [
            ...h,
            { sender: "user", message: customPrompt || "Generate insights based on selected data." },
        ]);
        setIsFetching(true);

        try {
            // Fetch only the data in our date range
            const [exp, inc] = await Promise.all([
                axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_expenses", {
                    params: { startDate: startDateStr, endDate: endDateStr },
                }),
                axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_incomes", {
                    params: { startDate: startDateStr, endDate: endDateStr },
                }),
            ]);

            const groupedExpenses = groupAndSummarizeData(exp.data, "Expense");
            const groupedIncomes = groupAndSummarizeData(inc.data, "Income");

            const prompt = `
You are an AI assistant specializing in financial analysis for K‑12 nutrition departments.
Provide **EXACTLY 50 words** for each of the five sections below.

Time Frame: ${startDateStr} through ${endDateStr}
Additional Guidance: ${customPrompt || "none"}

Grouped Expenses: ${JSON.stringify(groupedExpenses)}
Grouped Incomes: ${JSON.stringify(groupedIncomes)}

Sections:
1) Performance
2) Trends
3) Improvement
4) Excellence
5) Summary
      `.trim();

            const { data } = await axios.post(
                "https://aiendpoint.swancreate.com/api/ai/gpt",
                { prompt }
            );
            const aiText = data.response || "No insights generated.";

            setChatHistory((h) => [...h, { sender: "bot", message: aiText }]);
            setResponseDetails(parseResponse(aiText));
        } catch (err) {
            console.error(err);
            setChatHistory((h) => [
                ...h,
                { sender: "bot", message: "Failed to generate insights. Please try again." },
            ]);
        }

        setIsFetching(false);
    };

    const handlePrintReport = () => {
        const html = document.getElementById("financial-analysis-report")?.innerHTML;
        if (!html) {
            return alert("No report available to print.");
        }
        const backup = document.body.innerHTML;
        document.body.innerHTML = html;
        window.print();
        document.body.innerHTML = backup;
    };

    return (
        <Box
            sx={{
                padding: "20px",
                maxWidth: "800px",
                margin: "auto",
                backgroundColor: "#001f3f",
                borderRadius: "10px",
                color: "#ffffff",
            }}
        >
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 3, textAlign: "center" }}>
                Financial Insights
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography sx={{ mb: 1 }}>Select Month:</Typography>
                <Select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    sx={{ backgroundColor: "#ffffff", color: "#000000" }}
                >
                    {Array.from({ length: 12 }, (_, i) => {
                        const m = String(i + 1).padStart(2, "0");
                        return (
                            <MenuItem key={m} value={m}>
                                {new Date(0, i).toLocaleString("default", { month: "long" })}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography sx={{ mb: 1 }}>Select Year:</Typography>
                <Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    sx={{ backgroundColor: "#ffffff", color: "#000000" }}
                >
                    <MenuItem value="2024">2024</MenuItem>
                    <MenuItem value="2025">2025</MenuItem>
                </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography sx={{ mb: 1 }}>Custom Prompt (Optional):</Typography>
                <TextField
                    multiline
                    rows={4}
                    placeholder="Provide specific guidance, e.g., 'Focus on cost‑saving measures.'"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                />
            </FormControl>

            <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleGenerateInsights}
                disabled={isFetching}
                sx={{ mt: 2 }}
            >
                {isFetching ? <CircularProgress size={24} color="inherit" /> : "Generate Insights"}
            </Button>

            {responseDetails && (
                <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    onClick={handlePrintReport}
                    sx={{ mt: 2 }}
                >
                    Print Report
                </Button>
            )}

            {responseDetails && (
                <Box
                    id="financial-analysis-report"
                    sx={{
                        mt: 4,
                        backgroundColor: "#0B3D91",
                        borderRadius: "10px",
                        padding: "20px",
                    }}
                >
                    <FinancialAnalysisViewer response={responseDetails} />
                </Box>
            )}

            <Box
                ref={chatRef}
                sx={{
                    mt: 4,
                    maxHeight: "300px",
                    overflowY: "auto",
                    backgroundColor: "#00274d",
                    borderRadius: "10px",
                    padding: "10px",
                }}
            >
                {chatHistory.map((chat, i) =>
                    chat.sender === "user" ? (
                        <Box key={i} sx={{ mb: 1 }}>
                            <Typography sx={{ color: "#00bfff", fontWeight: "bold" }}>
                                User: {chat.message}
                            </Typography>
                        </Box>
                    ) : (
                        <Box key={i} sx={{ mb: 1 }}>
                            <Typography sx={{ color: "#ffffff", fontWeight: "bold" }}>
                                AI: {chat.message}
                            </Typography>
                        </Box>
                    )
                )}
            </Box>
        </Box>
    );
};

export default FinancialInsights;
